<template>
  <v-row
    align="center"
    justify="center"
  >
    <v-btn
      :color="!!activatorText ? 'third' : null"
      :outlined="!!activatorText"
      :plain="!activatorText"
      large
      rounded
      class="font-weight-bold my-3"
      :draggable="true"
      :disabled="disabled"
      @click="openPopUp"
      @dragstart="dragStart($event)"
      @drop="drop($event)"
      @dragover.prevent
    >
      {{ activatorText }}
      <v-icon size="24" :color="!!activatorText ? 'third' : null" right class="px-4">{{ icon }}</v-icon>
    </v-btn>

    <v-overlay
      :value="popup"
      opacity="0.5"
    >
      <v-card
        :style="hasWidth"
        light
      >
        <v-container class="px-5">
          <div v-if="!loading">
            <div class="full-width d-flex justify-end">
              <v-btn icon @click="closePopUp">
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </div>

            <HorseTableFilter v-if="isItemHorse" />

            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
              class="pb-4 pt-0 mt-0"
              @click.stop
              @keyup.enter="searchOnAlgolia"
            />

            <v-data-table
              id="tablepopup"
              v-model="newValue"
              :items="itemsList"
              :search="search"
              :hide-default-footer="true"
              :hide-default-header="!isItemHorse"
              :items-per-page="-1"
              :headers="headers"
              :height="tableHeight"
              show-select
              :item-key="contentTypeIdField"
              :custom-filter="filter"
              :single-select="singleSelect"
              @item-selected="selectItem"
              @click:row="rowClick"
            >
              <template #item.name="{ item }">
                <v-icon v-if="item.isOwner" color="third">mdi-crown</v-icon> 
                {{ item[field] }}
              </template>
              <template v-if="isItemHorse" #item.dailyEffort="{ item }">
                <v-col cols="12" class="light-pink-bg text-center">
                  {{ item.dailyEffort }}
                </v-col>
              </template>
              <template v-if="isItemHorse" #item.weeklyEffort="{ item }">
                <v-col cols="12" class="dark-pink-bg text-center">
                  {{ item.weeklyEffort }}
                </v-col>
              </template>
            </v-data-table>
            <div v-if="wtype === 'horse'" class="flex text-end mt-2">
              <v-btn
                color="third"
                outlined
                rounded
                class="font-weight-bold my-3"
                :draggable="true"
                :disabled="!newValue.length && singleSelect"
                @click="addRemoveResourceFromSession"
              >
                <v-icon size="24" color="third">mdi-plus</v-icon>
                Ajouter
              </v-btn>
            </div>
          </div>
          <div v-else>
            <div class="d-flex justify-center align-center" :style="{ height: tableHeight + 'px' }">
              <Loader />
            </div>
          </div>
        </v-container>
      </v-card>
    </v-overlay>
  </v-row>
</template>

<script>
import { ref, watch, computed, inject, onMounted } from '@vue/composition-api';
import { getContractWithStock } from '@/helpers/request';
import Loader from './common/Loader.vue';
import store from '@/store';
import { searchCustomerOnAlgolia } from '@/helpers/search';
import { checkHorseDailyEffort, computeDailyWeeklyHorseEffort} from '@/helpers/dataManager';

import HorseTableFilter from '@/components/ParticipantHorse/HorseTableFilter.vue';

export default {
  components: { Loader, HorseTableFilter },
  props: {
    value: {
      type: Array,
      default () {
        return [];
      }
    },
    list: {
      type: Array,
      default () {
        return [];
      }
    },
    icon: {
      type: String,
      default: ''
    },
    field: {
      type: String,
      default: ''
    },
    wtype: {
      type: String,
      default: ''
    },
    singleSelect: { type: Boolean },
    disabled: { type: Boolean, default: false },
    index: {
      type: Number,
      default: -1
    },
    width: {
      type: String,
      default: ''
    },
    activatorText: {
      type: String,
      default: null
    },
    contentTypeIdField: {
      type: String,
      default: null
    },
    session: {
      type: Object,
      default: null
    }
  },
  setup (props, context) {
    const search = ref(null);
    const popup = ref(false);

    const selectedHorseTypes = inject('selectedHorseTypes');
    const horseWithoutParticipant = inject('horseWithoutParticipant');

    const isItemHorse = computed(() => props.wtype === 'horse');

    const headers = computed(() => [
      { text: 'Nom', value: 'name', sortable: false },
      { text: 'Effort quotidien', value: 'dailyEffort', align: 'center', sortable: true },
      { text: 'Effort hebdomadaire', value: 'weeklyEffort', align: 'center', sortable: true }
    ]);

    const customerOwnedHorses = computed(() => {
      if(props.index === -1 || props.wtype !== 'horse' || !props.singleSelect) {
        return [];
      }
      return store.state.horses.filter(item => item.owners && item.owners.length && item.owners.some(owner => owner.ownerUuid === participantHorseList.value[props.index]?.participant))
        .map(horse => ({ isOwner: true, ...computeDailyWeeklyHorseEffort(horse) }));
    });

    const itemsList = computed(() => {
      if (props.wtype !== 'participant') {
        return [...customerOwnedHorses.value, ...props.list];
      }
      const customerList = [...props.list];
      const { algoliaCustomers } = store.state;
      
      const filteredCustomers = algoliaCustomers.filter(cust => !customerList.some(item => cust.uuid === item.uuid));
      
      if (filteredCustomers.length) {
        filteredCustomers.map(c => customerList.push(c));
      }
      return customerList;
    });

    const loading = ref(false);
    const newValue = ref(props.value);
    const updateSession = inject('updateSession');
    const selectParticipantModalData = inject('selectParticipantModalData');
    const showParticipantModal = inject('showParticipantModal');

    const tableHeight = computed(() => window.innerHeight * 0.5);

    const participantHorseList = inject('participantHorseList');

    const filter = (_, searchValue, item) => item[props.field].toLowerCase().includes(searchValue.toLowerCase());

    watch(() => props.value, () => {
      newValue.value = props.value;
    });

    watch(newValue, () => {
      context.emit('input', newValue.value);
    });

    const searchOnAlgolia = async () => {
      if (props.wtype !== 'participant') {
        return;
      }
      searchCustomerOnAlgolia(search.value);
    };

    const hasWidth = computed(() => (props.width ? `width:${props.width}px;` : null));

    const handleSingleSelect = async (item, isSelected, typeToLook) => {
      if (isSelected) {
        const participantHorseArray = JSON.parse(JSON.stringify(participantHorseList.value));
        // if horse already on session remove it first
        const index = participantHorseArray.findIndex((list, i) => props.index !== i && list[props.wtype] === item[props.contentTypeIdField]);

        if (index !== -1) {
          participantHorseArray[index][props.wtype] = null;

          if (!participantHorseArray[index][typeToLook]) {
            participantHorseArray.splice(index, 1);
          }
        }

        if (item.wtype === 'horse' && index === -1) {
          const checkForHorseEffort = await checkHorseDailyEffort([item], [props.session]);
          if (Object.keys(checkForHorseEffort).length) {
            store.commit('setAlertEffort', checkForHorseEffort);
            store.commit('setShowMaxEffortModal', true);
            store.commit('setMaxEffortCallback', () => {
              participantHorseArray[props.index][props.wtype] = item[props.contentTypeIdField];
              updateSession(participantHorseArray, props.singleSelect);
            });
            return;
          }
          
          participantHorseArray[props.index][props.wtype] = item[props.contentTypeIdField];
          updateSession(participantHorseArray, props.singleSelect);
          return;
        }
        participantHorseArray[props.index][props.wtype] = item[props.contentTypeIdField];
        updateSession(participantHorseArray, props.singleSelect);
      }
    };

    const handleMultiSelect = item => {
      // put all on newValue
      if (!newValue.value.some(i => i.uuid === item.uuid)) {
        newValue.value.push(item);
      }
    };

    const handleMultiDeselect = item => {
      // take all of newValue
      const index = newValue.value.findIndex(i => i.uuid === item.uuid);

      if (index !== -1) {
        newValue.value.splice(index, 1);
      }
    };

    const addRemoveResourceFromSession = async () => {
      const typeToLook = props.wtype === 'horse' ? 'participant' : 'horse';

      if(!newValue.value.length && props.singleSelect) {
        closePopUp();
        return;
      }

      const participantHorseArray = [];

      participantHorseList.value.map(pl => {
        const selectedItem = newValue.value.find(value => pl[props.wtype] === value.uuid);

        if (selectedItem || pl[typeToLook]) {
          participantHorseArray.push({
            ...pl,
            [props.wtype]: selectedItem ? selectedItem.uuid : null
          });
        }
      });

      newValue.value.map(value => {
        const isItemAlreadyOnList = participantHorseArray.some(item => item[props.wtype] === value.uuid);

        if (!isItemAlreadyOnList) {
          const participantHorsePair = {
            horse: props.wtype === 'horse' ? value.uuid : null,
            participant: props.wtype !== 'horse' ? value.uuid : null
          };

          if (value.wtype === 'customer') {
            participantHorsePair.isAnnonymousCustomer = value.isAnnonymousCustomer;
            participantHorsePair.customerDetail = value;
          }

          participantHorseArray.push(participantHorsePair);
        }
      });

      const horsesNotInSession = newValue.value.filter(item => !participantHorseList.value.some(pl => pl[props.wtype] === item.uuid));
      if (props.wtype === 'horse' && horsesNotInSession.length) {
        const checkForHorseEffort = await checkHorseDailyEffort(horsesNotInSession, [props.session]);
        if (Object.keys(checkForHorseEffort).length) {
          store.commit('setAlertEffort', checkForHorseEffort);
          store.commit('setShowMaxEffortModal', true);
          store.commit('setMaxEffortCallback', () => {
            participantHorseList.value = participantHorseArray;
            updateSession();
          });
          return;
        }
      }
      
      if (props.wtype === 'horse' && !props.singleSelect) {
        updateSession(participantHorseArray);
      } else {
        participantHorseList.value = participantHorseArray;
        updateSession();
      }
    };

    /**
     * value contains 2 props
     * value -> represent if the item was selected or not (true or false)
     * item-> represent the select item
     */
    const selectItem = async value => {
      const { item } = value;
      const isSelected = value.value;

      // if horse store into newValue only
      if (item.wtype === 'customer') {
        // if inconnue skip
        if (isSelected && !item.isAnnonymousCustomer) {
          loading.value = true;
          const contractsWhithStock = await getContractWithStock(item.uuid);
          selectParticipantModalData.value = { contracts: contractsWhithStock.data, user: item, session: props.session };
          showParticipantModal.value = true;
          loading.value = false;
          return;
        }
      }

      const typeToLook = props.wtype === 'horse' ? 'participant' : 'horse';
      // single select
      if (props.singleSelect) {
        return handleSingleSelect(item, isSelected, typeToLook);
      }

      // multi selected
      if (isSelected) {
        return handleMultiSelect(item, typeToLook);
      }

      return handleMultiDeselect(item, typeToLook);
    };

    const rowClick = (item, row) => {
      event.stopPropagation();
      row.select(!row.isSelected);
    };

    const openPopUp = () => {
      newValue.value = newValue.value.filter(v => participantHorseList.value.some(pl => pl[props.wtype] === v.uuid));

      if (props.singleSelect && isItemHorse.value && (participantHorseList.value || []).some(item => item.horse && !item.participant)) {
        horseWithoutParticipant.value = true;
      }

      popup.value = true;
    };

    const dragStart = event => {
      event.dataTransfer.setData('index', props.index);
    };

    const drop = event => {
      const indexDragged = event.dataTransfer.getData('index');

      participantHorseList.value[props.index][props.wtype] = participantHorseList.value[indexDragged][props.wtype];
      participantHorseList.value[indexDragged][props.wtype] = null;
      updateSession();
    };

    const closePopUp = () => {
      popup.value = false;
      selectedHorseTypes.value = [];
      horseWithoutParticipant.value = null;
    };

    return { addRemoveResourceFromSession, closePopUp, isItemHorse, loading, dragStart, drop, openPopUp, search, popup, headers, newValue, filter, selectItem, rowClick, hasWidth, searchOnAlgolia, itemsList, tableHeight };
  }
};
</script>

<style>
  #tablepopup > div > table{
    table-layout: auto;
  }
  #tablepopup tr {
    cursor: pointer;
  }
</style>
